'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSearch } from '@/components/data-table/data-table-search';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

import { getSubscriptionsTableColumns } from './subscriptions-table-columns';
import { SubscriptionsTableActionBar } from './subscriptions-table-action-bar';
import { SubscriptionsTableSkeleton } from './SubscriptionsTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import { Subscription } from '@/lib/api/subscriptions';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table';

interface SubscriptionsTableProps {
  subscriptions: Subscription[];
  total: number;
  options: {
    'subscriptions.status': string[];
    'stores.id': { id: string; name: string }[];
    preferredProcessorId: { id: string; name: string }[];
  };
  loading: boolean;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onTableStateChange: (state: MainTableState) => void;
  onCancelSubscription: (subscriptionId: string, cancelAtPeriodEnd?: boolean) => void;
  onResumeSubscription: (subscriptionId: string) => void;
  onPauseSubscription: (subscriptionId: string) => void;
  isCanceling: boolean;
  isResuming: boolean;
  isPausing: boolean;
  onRefetch: () => void;
  config?: TableConfig;
}

/**
 * SubscriptionsTable component for displaying subscriptions with DataTable functionality
 */
export function SubscriptionsTable({
  subscriptions,
  total,
  options,
  loading,
  searchQuery = '',
  onSearchChange,
  onTableStateChange,
  onCancelSubscription,
  onResumeSubscription,
  onPauseSubscription,
  isCanceling,
  isResuming,
  isPausing,
  onRefetch,
  config = {},
}: SubscriptionsTableProps) {
  const { t, i18n } = useTranslation('subscriptions');
  const navigate = useNavigate();
  const { orgRoutes } = useOrgNavigate();

  const handleRowClick = React.useCallback(
    (subscription: Subscription) => {
      navigate(orgRoutes.SUBSCRIPTION_DETAILS(subscription.id));
    },
    [navigate, orgRoutes],
  );

  // Memoize columns but recreate when options change
  // Use a simple count as dependency to ensure React detects changes
  const optionsCount =
    (options?.['subscriptions.status']?.length || 0) +
    (options?.['stores.id']?.length || 0) +
    (options?.preferredProcessorId?.length || 0);

  const columns = React.useMemo(
    () =>
      getSubscriptionsTableColumns({
        onCancelSubscription,
        onResumeSubscription,
        onPauseSubscription,
        isCanceling,
        isResuming,
        isPausing,
        options,
        t,
        orgRoutes,
      }),
    [
      onCancelSubscription,
      onResumeSubscription,
      onPauseSubscription,
      isCanceling,
      isResuming,
      isPausing,
      options,
      t,
      orgRoutes,
      i18n.language,
    ],
  );

  // Track if options have ever loaded (only changes once)
  const hasLoadedOptionsRef = React.useRef(false);
  if (!hasLoadedOptionsRef.current && optionsCount > 0) {
    hasLoadedOptionsRef.current = true;
  }
  const tableKey = `${hasLoadedOptionsRef.current ? 'loaded' : 'loading'}-${i18n.language}`;

  const { table } = useDataTable({
    data: subscriptions,
    columns,
    total,
    config,
    onTableStateChange,
    getRowId: (row: Subscription) => row.id,
  });
  return (
    <DataTable
      key={tableKey}
      table={table}
      actionBar={<SubscriptionsTableActionBar table={table} />}
      isLoading={loading}
      skeletonRows={<SubscriptionsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
      onRefresh={onRefetch}
      onRowClick={handleRowClick}
      extraFilters={<>
        <div className='w-full md:w-[200px]'>
          {onSearchChange && (
            <DataTableSearch value={searchQuery} onChange={onSearchChange} placeholder="Search anything..." />
          )}
        </div>
      </>}
    >

    </DataTable>
  );
}
